import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import type { Patient } from '@yui-receipt/shared'
import { api } from '@/lib/api'

type PatientListResponse = {
  patients: Patient[]
  total?: number
  limit?: number
  offset?: number
}

const PAGE_SIZE = 50

// Thai Buddhist (พ.ศ.) years appear in the imported DOBs — subtract 543 when
// we see a value >= 2400 so the computed age lines up with Gregorian years.
function ageFromDob(dob: string | null | undefined): number | null {
  if (!dob) return null
  const m = dob.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  let [_, yStr, moStr, dStr] = m
  let y = parseInt(yStr, 10)
  if (y >= 2400) y -= 543
  const mo = parseInt(moStr, 10) - 1
  const d = parseInt(dStr, 10)
  if (!Number.isFinite(y) || y <= 0) return null
  const now = new Date()
  let age = now.getUTCFullYear() - y
  const hadBirthday =
    now.getUTCMonth() > mo || (now.getUTCMonth() === mo && now.getUTCDate() >= d)
  if (!hadBirthday) age -= 1
  return age >= 0 && age < 130 ? age : null
}

export default function PatientListPage() {
  const [q, setQ] = useState('')
  const [page, setPage] = useState(0)

  // Reset to page 0 whenever the search query changes — otherwise we'd read
  // offset=300 on a filtered list that might only have 10 matches.
  useEffect(() => {
    setPage(0)
  }, [q])

  const { data, isLoading, error, refetch, isFetching } = useQuery<PatientListResponse>({
    queryKey: ['patients', q, page],
    queryFn: () => {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      params.set('limit', String(PAGE_SIZE))
      params.set('offset', String(page * PAGE_SIZE))
      return api<PatientListResponse>(`/patients?${params.toString()}`)
    },
  })

  const patients = data?.patients ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      <section className="card">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <h1 className="text-xl font-semibold">คนไข้</h1>
          <Link to="/patients/new" className="btn-primary min-h-tap">
            + เพิ่มคนไข้
          </Link>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="label">ค้นหา (ชื่อ / HN)</label>
            <input
              className="input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="เช่น สมชาย หรือ HN-2026-001"
            />
          </div>
          <button className="btn-secondary min-h-tap" onClick={() => refetch()}>
            {isFetching ? 'กำลังโหลด…' : '🔄 รีเฟรช'}
          </button>
        </div>
      </section>

      <section className="card">
        {isLoading ? (
          <p className="text-slate-500 text-center py-8">กำลังโหลด…</p>
        ) : error ? (
          <p className="text-rose-600 text-center py-8">โหลดไม่สำเร็จ</p>
        ) : patients.length === 0 ? (
          <p className="text-slate-500 text-center py-8">
            ยังไม่มีคนไข้ —{' '}
            <Link to="/patients/new" className="underline text-brand-700">
              เพิ่มคนแรก
            </Link>
          </p>
        ) : (
          <>
          <div className="mb-2 flex items-center justify-between text-xs text-slate-500">
            <span>
              ทั้งหมด {total.toLocaleString()} คน • หน้า {page + 1}/{totalPages}
            </span>
            {total > PAGE_SIZE && (
              <div className="flex gap-1">
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  disabled={page === 0 || isFetching}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                >
                  ← ก่อนหน้า
                </button>
                <button
                  type="button"
                  className="px-2 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                  disabled={page >= totalPages - 1 || isFetching}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  ถัดไป →
                </button>
              </div>
            )}
          </div>
          <ul className="divide-y divide-slate-100">
            {patients.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/patients/${p.id}`}
                  className="flex items-center justify-between py-3 px-2 hover:bg-slate-50 rounded-md gap-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {p.nameTh}
                      {(() => {
                        const age = ageFromDob(p.dob)
                        return age != null ? (
                          <span className="ml-2 text-xs text-slate-500 font-normal">
                            ({age} ปี)
                          </span>
                        ) : null
                      })()}
                    </div>
                    <div className="text-xs text-slate-500 font-mono">
                      HN {p.hn}
                      {p.phone ? ` • ${p.phone}` : ''}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {p.consentAudio ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        🎙 audio
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        🎙 off
                      </span>
                    )}
                    {p.consentAiScribe ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        🤖 AI
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">
                        🤖 off
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {total > PAGE_SIZE && (
            <div className="mt-3 flex items-center justify-center gap-1 text-xs">
              <button
                type="button"
                className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                disabled={page === 0 || isFetching}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
              >
                ← ก่อนหน้า
              </button>
              <span className="px-2 text-slate-500">
                หน้า {page + 1}/{totalPages}
              </span>
              <button
                type="button"
                className="px-3 py-1 rounded bg-slate-100 hover:bg-slate-200 disabled:opacity-40"
                disabled={page >= totalPages - 1 || isFetching}
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              >
                ถัดไป →
              </button>
            </div>
          )}
          </>
        )}
      </section>
    </div>
  )
}
